// Which sections of the dev panel are folded shut, remembered per browser.
//
// The panel grew a section every time the dev tools did, and at eleven it is a
// column taller than the window: every visit to the one control you wanted was
// a scroll past ten you did not. Each section now folds on its caption, and
// the fold is kept across reloads — a layout you have to redo every time the
// shell remounts is one nobody keeps.
//
// **The stored set is the *closed* ids, so a section defaults open.** The
// alternative — storing the open ones — reads a section added next week as
// closed on every browser that has ever touched the key, and a dev control
// that arrives hidden is a control nobody finds. It also keeps the smokes
// honest: a fresh profile sees every control, which is what they click.
//
// Pure, so the rule is tested without a window; `parts.tsx` owns the storage.

/** The key under `localStorage`. */
export const FOLDS_KEY = "factory-chess:dev-folds";

/** The closed ids, read off what the browser kept. Anything unreadable is nothing folded. */
export function parseFolds(raw: string | null | undefined): ReadonlySet<string> {
  if (!raw) return new Set();
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((entry): entry is string => typeof entry === "string"));
  } catch {
    return new Set();
  }
}

/** What the browser keeps. Sorted, so the same folds always write the same string. */
export function serialiseFolds(folded: ReadonlySet<string>): string {
  return JSON.stringify([...folded].sort());
}

/** The set with `id` flipped. A new set: the caller may be holding the old one. */
export function toggleFold(folded: ReadonlySet<string>, id: string): ReadonlySet<string> {
  const next = new Set(folded);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}
